import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useCategories } from '@/hooks/useCategories';
import { useUIStore } from '@/stores/uiStore';

export function Sidebar() {
  const { t } = useTranslation();
  const { isSidebarOpen, toggleSidebar } = useUIStore();
  const { data: categories, isLoading, error } = useCategories();
  const location = useLocation();

  return (
    <motion.aside
      className="hidden lg:flex flex-col bg-space-800/50 border-r border-cosmic-purple/20"
      animate={{ width: isSidebarOpen ? 256 : 64 }}
      transition={{ duration: 0.3 }}
    >
      {/* Toggle button */}
      <button
        onClick={toggleSidebar}
        className="flex items-center justify-center h-12 hover:bg-space-700 transition-colors border-b border-cosmic-purple/20"
        aria-label={isSidebarOpen ? t('sidebar.collapse') : t('sidebar.expand')}
      >
        {isSidebarOpen ? (
          <ChevronLeft className="w-5 h-5" />
        ) : (
          <ChevronRight className="w-5 h-5" />
        )}
      </button>

      {/* Categories */}
      <nav className="flex-1 overflow-y-auto py-4">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-cosmic-purple" />
          </div>
        )}

        {error && (
          <div className="px-4 py-2 text-red-400 text-sm">
            {t('sidebar.loadError')}
          </div>
        )}

        {categories?.map((category) => {
          const isActive = location.pathname === `/category/${category.slug}`;

          return (
            <Link
              key={category.id}
              to={`/category/${category.slug}`}
              className={`
                flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-all
                ${isActive
                  ? 'bg-cosmic-purple/20 text-white border border-cosmic-purple/40'
                  : 'text-gray-300 hover:bg-space-700 hover:text-white'
                }
              `}
            >
              <span className="text-2xl flex-shrink-0">{category.icon}</span>
              {isSidebarOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex-1 min-w-0"
                >
                  <div className="font-medium truncate">{category.name}</div>
                  <div className="text-xs text-gray-500">
                    {category.animals_count} {t('common.animals')}
                  </div>
                </motion.div>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      {isSidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 border-t border-cosmic-purple/20 text-center text-xs text-gray-500"
        >
          Zoo Galaxy v1.0
        </motion.div>
      )}
    </motion.aside>
  );
}
